/**
 * Tiptap (ProseMirror) doc JSON → markdown walker (Faz 4.2).
 *
 * Backend'deki `core/util/TiptapConverter.java` ile aynı EK C sözleşmesini
 * uygular; editör instance'ı GEREKTİRMEZ (store-level export, headless
 * VueNodeViewRenderer sorunları olmadan çalışır). smartBlock çıktısı EditorJS
 * yolundaki Jira-wiki panel biçimiyle uyumlu (`{panel:title=...}`) — CSV
 * export'un hedefi Jira import'u olduğu için.
 *
 * Kurallar (plan EK C):
 *  - Evrensel attrs (blockId, createdAuthor/At, textAlign…) okunmaz.
 *  - Boş blok `content` anahtarı olmadan gelir → null-guard.
 *  - Bilinmeyen node → çocuklarının düz metni (içerik kaybolmaz).
 *  - excalidraw / drawio (SVG data-URI taşır) / tableOfContents atlanır.
 */

/** `userStory` → `User Story` (FE smart-block.meta humanize kuralı). */
const humanize = (value) => {
  if (!value) return '';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const attr = (node, name, fallback = '') => {
  const value = node?.attrs?.[name];
  return value === null || value === undefined ? fallback : value;
};

// ─── Inline ──────────────────────────────────────────────────────

const applyMarks = (text, marks) => {
  if (!text || !Array.isArray(marks)) return text;
  let result = text;
  let href = null;
  for (const mark of marks) {
    switch (mark?.type) {
      case 'bold': result = `**${result}**`; break;
      case 'italic': result = `*${result}*`; break;
      case 'strike': result = `~~${result}~~`; break;
      case 'code': result = `\`${result}\``; break;
      case 'link': href = mark?.attrs?.href ?? null; break;
      default: break; // underline / highlight / textStyle → düşür
    }
  }
  if (href) result = `[${result}](${href})`;
  return result;
};

const inlineText = (content, markdown = true) => {
  if (!Array.isArray(content)) return '';
  let out = '';
  for (const node of content) {
    switch (node?.type) {
      case 'text':
        out += markdown ? applyMarks(node.text ?? '', node.marks) : (node.text ?? '');
        break;
      case 'hardBreak':
        out += '\n';
        break;
      case 'mention':
        out += `@${attr(node, 'label')}`;
        break;
      case 'pageLink':
        // İç referans — dış çıktıya URL üretilemez, label düz metin.
        out += attr(node, 'label');
        break;
      case 'linkMention': {
        const url = attr(node, 'url');
        const title = attr(node, 'title') || url;
        out += markdown && url ? `[${title}](${url})` : title;
        break;
      }
      default:
        out += inlineText(node?.content, markdown);
    }
  }
  return out.trim();
};

const plainText = (content) => inlineText(content, false);

// ─── Bloklar ─────────────────────────────────────────────────────

const listLines = (node, ordered, depth = 0) => {
  const lines = [];
  const items = node?.content;
  if (!Array.isArray(items)) return lines;
  let index = 1;
  for (const item of items) {
    let text = '';
    for (const child of item?.content ?? []) {
      const childType = child?.type;
      if ((childType === 'paragraph' || childType === 'heading') && !text) {
        text = inlineText(child.content);
      } else if (childType === 'bulletList' || childType === 'orderedList') {
        if (text) {
          lines.push(`${'  '.repeat(depth)}${ordered ? `${index++}. ` : '- '}${text}`);
          text = '';
        }
        lines.push(...listLines(child, childType === 'orderedList', depth + 1));
      }
    }
    if (text) {
      lines.push(`${'  '.repeat(depth)}${ordered ? `${index++}. ` : '- '}${text}`);
    }
  }
  return lines;
};

const taskListLines = (node, depth = 0) => {
  const lines = [];
  for (const item of node?.content ?? []) {
    if (item?.type !== 'taskItem') continue;
    const checked = item?.attrs?.checked === true;
    let text = '';
    for (const child of item?.content ?? []) {
      if (child?.type === 'paragraph' && !text) {
        text = inlineText(child.content);
      } else if (child?.type === 'taskList') {
        if (text) {
          lines.push(`${'  '.repeat(depth)}${checked ? '- [x] ' : '- [ ] '}${text}`);
          text = '';
        }
        lines.push(...taskListLines(child, depth + 1));
      }
    }
    if (text) {
      lines.push(`${'  '.repeat(depth)}${checked ? '- [x] ' : '- [ ] '}${text}`);
    }
  }
  return lines;
};

const tableMarkdown = (node) => {
  const rows = [];
  let rowIndex = 0;
  for (const row of node?.content ?? []) {
    const cells = [];
    let headerRow = false;
    for (const cell of row?.content ?? []) {
      if (cell?.type === 'tableHeader') headerRow = true;
      const cellParts = [];
      processBlocks(cell?.content, cellParts);
      cells.push(cellParts.join(' '));
    }
    rows.push(`| ${cells.join(' | ')} |`);
    if (rowIndex === 0 && headerRow) {
      rows.push(`| ${cells.map(() => '---').join(' | ')} |`);
    }
    rowIndex++;
  }
  return rows.join('\n');
};

const processBlock = (node, parts) => {
  switch (node?.type) {
    case 'paragraph': {
      const text = inlineText(node.content);
      if (text) parts.push(text);
      break;
    }
    case 'heading': {
      const text = inlineText(node.content);
      if (!text) break;
      const level = Math.min(Math.max(Number(attr(node, 'level', 2)) || 2, 1), 6);
      parts.push(`${'#'.repeat(level)} ${text}`);
      break;
    }
    case 'bulletList': {
      const lines = listLines(node, false);
      if (lines.length) parts.push(lines.join('\n'));
      break;
    }
    case 'orderedList': {
      const lines = listLines(node, true);
      if (lines.length) parts.push(lines.join('\n'));
      break;
    }
    case 'taskList': {
      const lines = taskListLines(node);
      if (lines.length) parts.push(lines.join('\n'));
      break;
    }
    case 'blockquote': {
      const inner = [];
      processBlocks(node.content, inner);
      if (inner.length) parts.push(`> ${inner.join('\n> ')}`);
      break;
    }
    case 'codeBlock': {
      const code = plainText(node.content);
      if (code) parts.push(`\`\`\`${attr(node, 'language')}\n${code}\n\`\`\``);
      break;
    }
    case 'horizontalRule':
      parts.push('---');
      break;
    case 'table': {
      const table = tableMarkdown(node);
      if (table) parts.push(table);
      break;
    }
    case 'details': {
      for (const child of node?.content ?? []) {
        if (child?.type === 'detailsSummary') {
          const summary = inlineText(child.content);
          if (summary) parts.push(`**${summary}**`);
        } else if (child?.type === 'detailsContent') {
          processBlocks(child.content, parts);
        }
      }
      break;
    }
    case 'smartBlock': {
      // EditorJS yolundaki Jira-wiki panel biçimiyle uyumlu çıktı.
      const title = humanize(attr(node, 'kind'));
      const inner = [];
      processBlocks(node.content, inner);
      let panel = '';
      if (title) panel += `{panel:title=${title}|bgColor=#E8E7FF}\n`;
      panel += inner.join('\n');
      if (title) panel += '\n{panel}';
      parts.push(panel);
      break;
    }
    case 'callout': {
      const inner = [];
      processBlocks(node.content, inner);
      if (inner.length) {
        const variant = humanize(attr(node, 'variant', 'info'));
        parts.push(`> **${variant}:** ${inner.join('\n> ')}`);
      }
      break;
    }
    case 'image': {
      const src = attr(node, 'src');
      const label = attr(node, 'caption') || attr(node, 'alt');
      if (src) parts.push(`![${label}](${src})`);
      break;
    }
    case 'linkCard': {
      const url = attr(node, 'url');
      if (url) parts.push(`[${attr(node, 'title') || url}](${url})`);
      break;
    }
    case 'embed': {
      const url = attr(node, 'url');
      if (url) parts.push(`[${attr(node, 'provider') || 'embed'}](${url})`);
      break;
    }
    case 'screenLink': {
      const imageUrl = attr(node, 'imageUrl');
      const label = attr(node, 'label');
      if (imageUrl) parts.push(`![${label}](${imageUrl})`);
      else if (label) parts.push(label);
      break;
    }
    case 'diagram':
      // Kaynak child codeBlock'ta yaşar (EK C).
      processBlocks(node.content, parts);
      break;
    case 'excalidraw':
    case 'drawio':
    case 'tableOfContents':
      break; // SVG data-URI / verisiz — çıktıya sızdırılmaz
    default: {
      const text = inlineText(node?.content);
      if (text) parts.push(text);
    }
  }
};

const processBlocks = (content, parts) => {
  if (!Array.isArray(content)) return;
  for (const node of content) processBlock(node, parts);
};

/** Tiptap doc JSON'unu markdown'a çevirir. */
export const convertTiptapToMarkdown = (doc) => {
  const parts = [];
  processBlocks(doc?.content, parts);
  return parts.join('\n\n');
};

/** Discriminator: obje Tiptap doc mu? */
export const isTiptapDoc = (data) =>
  !!data && typeof data === 'object' && data.type === 'doc';

/**
 * Tiptap doc'undan hedef smartBlock'un (attrs.kind) içerik satırlarını çıkarır
 * (tree export'un User Story / Acceptance Criteria bölümleri için).
 * Dönen dizi: her satır düz metin (mark'sız).
 */
export const extractSmartBlockLines = (doc, kind) => {
  if (!isTiptapDoc(doc)) return [];
  for (const node of doc.content ?? []) {
    if (node?.type !== 'smartBlock' || attr(node, 'kind') !== kind) continue;
    const lines = [];
    for (const child of node.content ?? []) {
      if (child?.type === 'taskList') {
        for (const item of child.content ?? []) {
          const text = plainText(item?.content?.[0]?.content);
          if (text) lines.push(text);
        }
      } else {
        const parts = [];
        processBlock(child, parts);
        // Markdown süslemesi tree export'ta istenmez — düz satırlara indir.
        for (const part of parts) {
          for (const line of part.split('\n')) {
            const clean = line.replace(/^#+\s+|^[-*]\s+|\*\*/g, '').trim();
            if (clean) lines.push(clean);
          }
        }
      }
    }
    return lines;
  }
  return [];
};
